import React, { useState } from 'react';
import { StepProps, SPECIALTY_COLORS } from '../../types';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
export function DriversBarriers({
  state,
  updateState,
  nextStep
}: StepProps) {
  const role = state.selectedSpecialty || 'Nephrologist';
  const color = SPECIALTY_COLORS[role];
  const [showDriverData, setShowDriverData] = useState(!!state.predictedDriver);
  const [showBarrierData, setShowBarrierData] = useState(!!state.predictedBarrier);
  const drivers = ['Safety data', 'Outcome evidence', 'Guidelines', 'Peer behavior'];
  const barriers = ['Safety concerns', 'Lack of guidance', 'Workload', 'Late referrals'];
  // Mock data for charts
  const getDriverData = () => {
    switch (role) {
      case 'Nephrologist':
        return [90, 60, 70, 40];
      // Safety driven
      case 'Cardiologist':
        return [40, 95, 60, 50];
      // Outcome driven
      case 'Endocrinologist':
        return [60, 80, 85, 50];
      // Guideline/Data driven
      case 'PCP':
        return [70, 50, 90, 85];
      // Guideline/Peer driven
      default:
        return [50, 50, 50, 50];
    }
  };
  const getBarrierData = () => {
    switch (role) {
      case 'Nephrologist':
        return [85, 40, 70, 90];
      // Late referrals & safety
      case 'Cardiologist':
        return [30, 50, 80, 20];
      // Workload
      case 'Endocrinologist':
        return [60, 75, 65, 40];
      // Guidance
      case 'PCP':
        return [70, 90, 85, 30];
      // Guidance & workload
      default:
        return [50, 50, 50, 50];
    }
  };
  const handleDriverSelect = (opt: string) => {
    updateState({
      predictedDriver: opt
    });
    setTimeout(() => setShowDriverData(true), 500);
  };
  const handleBarrierSelect = (opt: string) => {
    updateState({
      predictedBarrier: opt
    });
    setTimeout(() => setShowBarrierData(true), 500);
  };
  return <div className="flex-grow flex flex-col py-12 px-6 overflow-y-auto" data-id="element-251">
      <div className="max-w-4xl w-full mx-auto" data-id="element-252">
        <div className="text-center mb-12" data-id="element-253">
          <h2 className="text-3xl md:text-5xl font-bold text-dark" data-id="element-254">
            What drives and blocks {role}s?
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12" data-id="element-255">
          {/* DRIVERS SECTION */}
          <div data-id="element-256">
            <h3 className="text-xl font-bold mb-6" data-id="element-257">
              What is their PRIMARY driver?
            </h3>
            <div className="space-y-3 mb-6" data-id="element-258">
              {drivers.map(opt => {
              const isSelected = state.predictedDriver === opt;
              const isFaded = showDriverData && !isSelected;
              return <motion.button key={opt} whileHover={!showDriverData ? {
                scale: 1.02
              } : {}} whileTap={!showDriverData ? {
                scale: 0.98
              } : {}} onClick={() => handleDriverSelect(opt)} disabled={showDriverData} className={`
                      w-full p-4 rounded-xl border-2 text-left transition-all duration-300
                      ${isSelected ? 'bg-gray-50' : 'bg-white border-gray-200 hover:border-gray-300'}
                      ${isFaded ? 'opacity-40 grayscale' : 'opacity-100'}
                    `} style={{
                borderColor: isSelected ? color : undefined
              }} data-id="element-259">
                    <span className="font-medium text-dark" data-id="element-260">{opt}</span>
                  </motion.button>;
            })}
            </div>

            <AnimatePresence data-id="element-261">
              {showDriverData && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} className="bg-gray-50 rounded-2xl p-6 border border-gray-100 overflow-hidden" data-id="element-262">
                  <h4 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-4" data-id="element-263">
                    Observed Drivers
                  </h4>
                  <div className="space-y-4" data-id="element-264">
                    {drivers.map((opt, i) => <div key={opt} data-id="element-265">
                        <div className="flex justify-between text-xs font-medium mb-1" data-id="element-266">
                          <span data-id="element-267">{opt}</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-268">
                          <motion.div className="h-full rounded-full" style={{
                      backgroundColor: color
                    }} initial={{
                      width: 0
                    }} animate={{
                      width: `${getDriverData()[i]}%`
                    }} transition={{
                      duration: 1,
                      delay: 0.2 + i * 0.1
                    }} data-id="element-269" />
                        </div>
                      </div>)}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* BARRIERS SECTION */}
          <div className={!showDriverData ? 'opacity-50 pointer-events-none transition-opacity' : 'transition-opacity'} data-id="element-270">
            <h3 className="text-xl font-bold mb-6" data-id="element-271">
              What is their BIGGEST barrier?
            </h3>
            <div className="space-y-3 mb-6" data-id="element-272">
              {barriers.map(opt => {
              const isSelected = state.predictedBarrier === opt;
              const isFaded = showBarrierData && !isSelected;
              return <motion.button key={opt} whileHover={!showBarrierData ? {
                scale: 1.02
              } : {}} whileTap={!showBarrierData ? {
                scale: 0.98
              } : {}} onClick={() => handleBarrierSelect(opt)} disabled={showBarrierData} className={`
                      w-full p-4 rounded-xl border-2 text-left transition-all duration-300
                      ${isSelected ? 'bg-gray-50' : 'bg-white border-gray-200 hover:border-gray-300'}
                      ${isFaded ? 'opacity-40 grayscale' : 'opacity-100'}
                    `} style={{
                borderColor: isSelected ? color : undefined
              }} data-id="element-273">
                    <span className="font-medium text-dark" data-id="element-274">{opt}</span>
                  </motion.button>;
            })}
            </div>

            <AnimatePresence data-id="element-275">
              {showBarrierData && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} className="bg-gray-50 rounded-2xl p-6 border border-gray-100 overflow-hidden" data-id="element-276">
                  <h4 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-4" data-id="element-277">
                    Observed Barriers
                  </h4>
                  <div className="space-y-4" data-id="element-278">
                    {barriers.map((opt, i) => <div key={opt} data-id="element-279">
                        <div className="flex justify-between text-xs font-medium mb-1" data-id="element-280">
                          <span data-id="element-281">{opt}</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-282">
                          <motion.div className="h-full rounded-full" style={{
                      backgroundColor: color
                    }} initial={{
                      width: 0
                    }} animate={{
                      width: `${getBarrierData()[i]}%`
                    }} transition={{
                      duration: 1,
                      delay: 0.2 + i * 0.1
                    }} data-id="element-283" />
                        </div>
                      </div>)}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>

        <div className="flex justify-center pb-8 h-16" data-id="element-284">
          {showDriverData && showBarrierData && <motion.button initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} onClick={nextStep} className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-dark rounded-full hover:bg-black transition-colors" data-id="element-285">
              See Cross-Specialty Data
              <ArrowRight className="ml-2 w-5 h-5" data-id="element-286" />
            </motion.button>}
        </div>
      </div>
    </div>;
}